//Include Both Helper File with needed methods
import { getFirebaseBackend } from "../../../helpers/firebase_helper";
import { buildApiUrl } from "../../../helpers/apiUrl";
import {
  postFakeLogin,
  postJwtLogin,
  postGoogleLogin,
} from "../../../helpers/fakebackend_helper";

import { loginSuccess, logoutUserSuccess, apiError, reset_login_flag, setLoading } from './reducer';

export const loginUser = (user, history, rememberMe = false) => async (dispatch) => {
  
  try {
    dispatch(setLoading(true));
    console.log("Login attempt with:", user.email);
    
    let response;
    if (process.env.REACT_APP_DEFAULTAUTH === "firebase") {
      let fireBaseBackend = getFirebaseBackend();
      response = fireBaseBackend.loginUser(
        user.email,
        user.password
      );
    } else if (process.env.REACT_APP_DEFAULTAUTH === "jwt") {
      response = postJwtLogin({
        email: user.email,
        password: user.password
      });

    } else {
      // For real backend API
      const endpoint = rememberMe ? '/auth/login-remember-me' : '/auth/login';
      response = postFakeLogin({
        email: user.email,
        password: user.password,
      }, endpoint);
    }
    
    var data = await response;
    console.log("Login response:", data);

    if (data) {
      // Ensure isAdmin field is set in the response data
      if (!data.isAdmin && data.role) {
        data.isAdmin = data.role === 'admin' || data.role === 'ADMIN';
      }
      
      sessionStorage.setItem("authUser", JSON.stringify(data));
      // Force AuthProtected to re-fetch /feature-flags for the new session
      sessionStorage.removeItem("featureFlagsLoaded");
      // Track total successful logins so the dashboard's "Getting Started"
      // surface can downgrade itself: full wizard at login #1, compact link
      // at logins #2–#5, and hidden afterwards. Always available via the
      // header More menu → Getting Started.
      try {
        const prev = parseInt(localStorage.getItem("lw_login_count") || "0", 10) || 0;
        localStorage.setItem("lw_login_count", String(prev + 1));
      } catch (_) { /* localStorage unavailable */ }
      // If rememberMe, persist to localStorage for auto-login
      if (rememberMe) {
        localStorage.setItem("rememberAuthUser", JSON.stringify(data));
      }
      
      // Handle response with token field (direct from API)
      if (data.token) {
        console.log("Login successful, token received");
        dispatch(loginSuccess(data));

        // Admin users go straight to admin dashboard
        if (data.isAdmin) {
          console.log("Admin user detected, redirecting to /admin");
          history('/admin');
          return;
        }

        // Always send users to /dashboard. AuthProtected will bounce them to
        // /onboarding if they haven't completed setup, and the dashboard's
        // OnboardingWizard renders the appropriate "Getting Started" steps
        // based on their userMode.
        history('/dashboard');
      } else if (data.status === "success" || data.data) {
        // Alternative response formats
        const userData = data.data || data;

        // Ensure isAdmin field is set
        if (!userData.isAdmin && userData.role) {
          userData.isAdmin = userData.role === 'admin' || userData.role === 'ADMIN';
        }

        console.log("Login successful with alternative format");
        dispatch(loginSuccess(userData));

        if (userData.isAdmin) {
          console.log("Admin user detected (alt format), redirecting to /admin");
          history('/admin');
          return;
        }

        history('/dashboard');
      } else {
        console.log("Login failed, no token in response");
        dispatch(apiError(data.message || "Invalid email or password"));
      }
    }
  } catch (error) {
    // Error will be a string message from the api_helper interceptor
    console.error("Login error caught:", error);
    const errorMessage = typeof error === 'string' ? error : (error?.message || "Invalid email or password");
    console.log("Dispatching error:", errorMessage);
    dispatch(apiError(errorMessage));
  }
};

/**
 * Check if user is a first-time user by verifying they have no customers AND no reminders
 */
const checkFirstTimeLogin = async (token) => {
  try {
    // Fetch customers
    const customersRes = await fetch(buildApiUrl('/customers'), {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    const customersData = customersRes.ok ? await customersRes.json() : [];
    const customersList = Array.isArray(customersData) ? customersData : (customersData.data || []);
    
    // Fetch reminders
    const remindersRes = await fetch(buildApiUrl('/reminders'), {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    const remindersData = remindersRes.ok ? await remindersRes.json() : [];
    const remindersList = remindersData.data ? remindersData.data : (Array.isArray(remindersData) ? remindersData : []);
    
    // User is first-time if they have NO customers AND NO reminders
    const isFirstTime = customersList.length === 0 && remindersList.length === 0;
    console.log(`First-time check: customers=${customersList.length}, reminders=${remindersList.length}, isFirstTime=${isFirstTime}`);
    
    return isFirstTime;
  } catch (error) {
    console.error('Error checking first-time login:', error);
    // On error, assume returning user (default to dashboard)
    return false;
  }
};

export const logoutUser = () => async (dispatch) => {
  try {
    sessionStorage.removeItem("authUser");
    sessionStorage.removeItem("featureFlagsLoaded");
    localStorage.removeItem("rememberAuthUser");
    let fireBaseBackend = getFirebaseBackend();
    if (process.env.REACT_APP_DEFAULTAUTH === "firebase") {
      const response = fireBaseBackend.logout;
      dispatch(logoutUserSuccess(response));
    } else {
      dispatch(logoutUserSuccess(true));
    }

  } catch (error) {
    dispatch(apiError(error));
  }
};

export const socialLogin = (type, history, googleToken) => async (dispatch) => {
  try {
    let response;

    if (process.env.REACT_APP_DEFAULTAUTH === "firebase") {
      const fireBaseBackend = getFirebaseBackend();
      response = fireBaseBackend.socialLoginUser(type);
    } else if (type === "google" && googleToken) {
      // For Google OAuth2 login with real backend
      response = postGoogleLogin({ token: googleToken });
    }
    
    const socialdata = await response;
    if (socialdata) {
      sessionStorage.setItem("authUser", JSON.stringify(socialdata));
      dispatch(loginSuccess(socialdata));
      
      // Admin users go straight to admin dashboard
      if (socialdata.isAdmin) {
        console.log("Admin user detected (OAuth), redirecting to /admin");
        history('/admin');
        return;
      }
      
      // Check if this is a first-time user
      const token = socialdata.token || socialdata.data?.token;
      if (token) {
        checkFirstTimeLogin(token).then(isFirstTime => {
          if (isFirstTime) {
            console.log("First-time user detected (OAuth), redirecting to /add-task");
            sessionStorage.setItem("firstLoginRedirectDone", "false");
            history('/add-task');
          } else {
            console.log("Returning user (OAuth), redirecting to /dashboard");
            history('/dashboard');
          }
        });
      } else {
        // Default to dashboard if no token found
        history('/dashboard');
      }
    }

  } catch (error) {
    dispatch(apiError(error));
  }
};

export const resetLoginFlag = () => (dispatch) => {
  dispatch(reset_login_flag());
};